import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import appLogo from '../assets/branding/logo.png';

export default function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => setIsMenuOpen(false), [location.pathname]);

  const isActive = (path) => location.pathname === path;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/80 bg-white/92 backdrop-blur-lg">
      <nav className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link to="/" className="group flex min-w-0 items-center gap-2.5">
          <img
            src={appLogo}
            alt="Tech Dynasty"
            className="h-9 w-9 shrink-0 rounded-xl object-cover"
          />
          <span className="truncate text-base font-black text-slate-900 sm:text-lg">
            Tech Dynasty
          </span>
        </Link>

        <div className="hidden items-center gap-1 md:flex">
          <Link
            to="/"
            className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
              isActive('/') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            Home
          </Link>
          <Link
            to="/download"
            className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
              isActive('/download') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            Download
          </Link>
          <Link
            to="/support"
            className={`relative rounded-lg px-3 py-2 text-sm font-semibold transition ${
              isActive('/support') ? 'text-[var(--brand-primary)]' : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
            }`}
          >
            Support
          </Link>
        </div>

        <div className="hidden items-center gap-2 md:flex">
          {isAuthenticated ? (
            <>
              <Link
                to="/profile"
                className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-accent)] text-xs font-black text-white">
                  {(user?.name || user?.email || '?')[0].toUpperCase()}
                </span>
                <span className="max-w-[140px] truncate">{user?.name || user?.email}</span>
              </Link>
              <button
                onClick={handleLogout}
                className="rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50"
              >
                Log out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="rounded-xl px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-100">
                Log in
              </Link>
              <Link
                to="/register"
                className="rounded-xl bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-accent)] px-4 py-2 text-sm font-bold text-white shadow-md shadow-slate-900/10 transition hover:-translate-y-px"
              >
                Get started
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setIsMenuOpen((v) => !v)}
          className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 md:hidden"
          aria-label="Toggle menu"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            {isMenuOpen
              ? <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              : <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />}
          </svg>
        </button>
      </nav>

      {isMenuOpen && (
        <div className="border-t border-slate-100 bg-white/98 backdrop-blur-lg md:hidden">
          <div className="space-y-1 px-4 py-4">
            <Link
              to="/"
              className={`block rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                isActive('/') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              Home
            </Link>
            <Link
              to="/download"
              className={`block rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                isActive('/download') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              Download
            </Link>
            <Link
              to="/support"
              className={`block rounded-xl px-3 py-2.5 text-sm font-bold transition ${
                isActive('/support') ? 'bg-[var(--brand-bg)] text-[var(--brand-primary)]' : 'text-slate-700 hover:bg-slate-50'
              }`}
            >
              Support
            </Link>
            <div className="mt-3 border-t border-slate-100 pt-3">
              {isAuthenticated ? (
                <>
                  <Link
                    to="/profile"
                    className="flex items-center gap-2 rounded-xl px-3 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50"
                  >
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-[var(--brand-primary)] to-[var(--brand-accent)] text-xs font-black text-white">
                      {(user?.name || user?.email || '?')[0].toUpperCase()}
                    </span>
                    <span className="truncate">{user?.name || user?.email}</span>
                  </Link>
                  <button
                    onClick={handleLogout}
                    className="mt-1 block w-full rounded-xl px-3 py-2.5 text-left text-sm font-bold text-slate-700 hover:bg-slate-50"
                  >
                    Log out
                  </button>
                </>
              ) : (
                <div className="grid grid-cols-2 gap-2">
                  <Link to="/login" className="rounded-xl border border-slate-200 py-2.5 text-center text-sm font-bold text-slate-700 hover:bg-slate-50">
                    Log in
                  </Link>
                  <Link to="/register" className="rounded-xl bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-accent)] py-2.5 text-center text-sm font-bold text-white">
                    Get started
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
